import { type PropsWithChildren, useCallback, useState } from 'react';
import { MDText } from 'i18n-react';

import cypressIds from 'Components/cypress_ids.json';
import type { VehicleBidding, VehicleState } from 'Pages/vehicles/helpers/vehicle.schema';
import { triggerCancelOfferOrBidClickedEvent } from 'Services/analytics/events';
import { applyCypressData } from 'Utilities/helpers';

import { CancelModal } from '../../CancelModal/CancelModal';
import { TextCTA } from '../../TextCTA/TextCTA';
import texts from '../BiddingSectionCTAStateController.texts';
import { getSaleType } from '../getSaleType';
import { useCancelBidOrOffer } from '../useCancelBidOrOffer';

import styles from './UserHasBidOrOffered.module.scss';

const LocalT = new MDText(texts.userHasBidOrOffered);

interface UserHasBidOrOfferedProps {
	latestUserBidOrOffer: NonNullable<VehicleBidding['latestUserBidOrOffer']>;
	state: VehicleState;
}

export const UserHasBidOrOffered: React.FC<PropsWithChildren<UserHasBidOrOfferedProps>> = ({
	children,
	latestUserBidOrOffer,
	state,
}) => {
	const saleType = getSaleType(state);
	const [shouldShowCancellationModal, setShouldShowCancellationModal] = useState(false);

	const purchaseTypeLabel = `${LocalT.translate(`cancellationModal.eventAndToastLabel.${saleType}`)}`;

	const { bidOrOfferAmount } = latestUserBidOrOffer;

	const { isCancellationInProgress, onCancelBidOrOffer } = useCancelBidOrOffer();

	const handleToggleCancellationModal = () => {
		setShouldShowCancellationModal(!shouldShowCancellationModal);
	};

	const handleCancelBidOrOffer = () => {
		triggerCancelOfferOrBidClickedEvent(purchaseTypeLabel);
		handleToggleCancellationModal();
	};

	const handleCancelConfirmation = useCallback(async () => {
		await onCancelBidOrOffer();

		setShouldShowCancellationModal(false);
	}, [onCancelBidOrOffer]);

	const isBINVehicle = saleType === 'SALE';

	return (
		<div className={styles.container} {...applyCypressData(cypressIds.containers.cancelBidContainer)}>
			<TextCTA
				buttonProps={{
					text: `${LocalT.translate(`cancelButton.${saleType}`)}`,
					thcButtonProps: { onClick: handleCancelBidOrOffer, size: 'small', variant: 'secondary' },
				}}
				title={LocalT.translate(`title.${saleType}`, { amount: bidOrOfferAmount.displayValue })}
			/>
			{/* BIN vehicles still have CTAs even when an offer has been made */}
			{isBINVehicle ? children : null}
			{shouldShowCancellationModal && (
				<CancelModal
					bidOrOfferAmountDisplayValue={bidOrOfferAmount.displayValue}
					dismissModal={handleToggleCancellationModal}
					isCancellationInProgress={isCancellationInProgress}
					onCancel={handleCancelConfirmation}
					purchaseTypeLabel={purchaseTypeLabel}
					saleType={saleType}
				/>
			)}
		</div>
	);
};
